import {yupResolver} from "@hookform/resolvers/yup";
import omit from "lodash/omit";
import {useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import {dynamicTabConfig, staticTabConfig, TabConfig} from "@/components/layout/TabContentContainer.types.ts";
import {TabForm} from "@/components/TabForm/TabForm.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {ContactGroup} from "@/lib/types/contacts.ts";
import {
  ContactInfoFormValues,
  EditContactInfoForm,
  EditContactInfoSchema,
} from "@/pages/contacts/components/form/EditContactInfoForm.tsx";
import {
  EditContactOnCallHoursForm,
  EditContactOnCallHoursSchema,
  OnCallHoursFormValues,
} from "@/pages/contacts/components/form/EditContactOnCallHoursForm.tsx";
import {
  ContactTabs,
  generateDefaultContactGroupFormValues,
  IntegrationProfile,
  PushNotificationProfile,
} from "@/pages/contacts/components/form/types.ts";

const EditContactSchema = Yup.object().concat(EditContactInfoSchema).concat(EditContactOnCallHoursSchema);

const defaultContactFormConfig: Record<ContactTabs, dynamicTabConfig> = {
  [ContactTabs.CONTACT_INFO]: {
    isVisible: true,
  },
  [ContactTabs.ON_CALL_HOURS]: {
    isVisible: true,
  },
};

interface EditContactFormProps {
  title: string;
  onSubmit: (data: ContactFormValues) => Promise<void>;
  onCancel: () => void;
  defaultValues?: ContactGroup;
  formId: string;
  disabled?: boolean;
  serverErrors?: ServerErrors;
  integrationProfiles: IntegrationProfile[];
  pushNotificationProfiles: PushNotificationProfile[];
}

export interface ContactFormValues extends ContactInfoFormValues, OnCallHoursFormValues {}

export const EditContactForm = ({
  title,
  formId,
  onSubmit,
  onCancel,
  defaultValues,
  serverErrors,
  integrationProfiles,
  pushNotificationProfiles,
}: EditContactFormProps) => {
  const [tab, setTab] = useState(ContactTabs.CONTACT_INFO as string);
  const [tabState] = useState<Record<ContactTabs, dynamicTabConfig>>(defaultContactFormConfig);
  const methods = useForm<ContactFormValues>({
    resolver: yupResolver<ContactFormValues>(EditContactSchema),
    defaultValues: omit(
      defaultValues ? defaultValues : generateDefaultContactGroupFormValues(),
      "num_services",
      "num_escalations"
    ),
  });
  const {handleSubmit, setError} = methods;

  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, messages]) => {
        if (field !== "__all__") {
          setError(field as keyof ContactFormValues, {
            message: Array.isArray(messages) ? messages.join("\n") : String(messages),
          });
        }
      });
    }
  }, [serverErrors, setError]);

  const contactFormTabConfig: Record<ContactTabs, staticTabConfig> = useMemo(
    () => ({
      [ContactTabs.CONTACT_INFO]: {
        id: ContactTabs.CONTACT_INFO,
        title: "Contact Info",
        fieldList: Object.keys(EditContactInfoSchema.describe().fields),
        html: () => (
          <EditContactInfoForm
            integrationProfiles={integrationProfiles}
            pushNotificationProfiles={pushNotificationProfiles}
          />
        ),
      },
      [ContactTabs.ON_CALL_HOURS]: {
        id: ContactTabs.ON_CALL_HOURS,
        title: "On-Call Hours",
        fieldList: Object.keys(EditContactOnCallHoursSchema.describe().fields),
        html: () => <EditContactOnCallHoursForm />,
      },
    }),
    [integrationProfiles, pushNotificationProfiles]
  );

  const sections: Record<string, TabConfig> = useMemo(() => {
    const derivedSections: Record<string, TabConfig> = {...contactFormTabConfig};
    Object.keys(derivedSections).forEach((key) => {
      const tabKey = key as ContactTabs;
      derivedSections[tabKey] = {
        ...derivedSections[tabKey],
        ...tabState[tabKey],
      };
    });
    return derivedSections;
  }, [contactFormTabConfig, tabState]);

  const contactFormFieldToMap: Record<string, ContactTabs> = useMemo(
    () =>
      Object.values(contactFormTabConfig).reduce((acc: Record<string, ContactTabs>, section) => {
        if (section.fieldList) {
          section.fieldList.forEach((field: string) => {
            acc[field] = section.id as ContactTabs;
          });
        }
        return acc;
      }, {}),
    [contactFormTabConfig]
  );

  return (
    <FormProvider {...methods}>
      <TabForm
        title={title}
        tab={tab}
        setTab={setTab}
        sections={sections}
        variant="modal"
        formId={formId}
        submitText="Save"
        fieldToTabMap={contactFormFieldToMap}
        goBack={onCancel}
        serverErrorsState={serverErrors}
      >
        <form id={formId} onSubmit={handleSubmit(onSubmit, console.error)}>
          {sections[tab].html()}
        </form>
      </TabForm>
    </FormProvider>
  );
};
